import React, { useState, useEffect, useRef } from "react";
import { uploadImageApi } from "../../services/coreService";
import { useTranslation } from "react-i18next";
import { getOptimizedBase64ForUpload } from "../../utils/imageUploadOptimizer";

export default function DocumentUploadModal({
    open,
    onClose,
    initialImage,
    userId,
    onUpload,
    title = "Upload Document",
    accept = "image/*"
}) {
    const { t } = useTranslation();
    const [imageUrl, setImageUrl] = useState(initialImage || "");
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");
    const [fileName, setFileName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef();

    useEffect(() => {
        setImageUrl(initialImage || "");
        setMessage("");
        setFileName("");
        setSelectedFile(null);
        setPreviewUrl(null);
    }, [initialImage, open]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        setSelectedFile(file);
        setMessage("");
        
        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        setMessage("");
        try {
            const base64 = await getOptimizedBase64ForUpload(selectedFile, {
                maxWidthOrHeight: 1920,
                maxBytes: 700 * 1024,
                initialQuality: 0.9,
                minQuality: 0.7,
            });
            console.log('Starting upload for user:', userId);
            const res = await uploadImageApi(userId, base64);
            console.log('Upload response:', res);
            if (res && res.responseCode === 200 && res.responseData) {
                setImageUrl(res.responseData);
                setMessage("Document uploaded successfully!");
                onUpload(res.responseData);
                setSelectedFile(null);
                setFileName("");
                setPreviewUrl(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
            } else {
                const errorMsg = res?.responseMessage || res?.message || "Upload failed. Please try again.";
                console.error('Upload failed with response:', res);
                setMessage(errorMsg);
            }
        } catch (err) {
            console.error('Upload error caught:', err);
            setMessage(`Upload failed: ${err.message || 'Please check your connection and try again.'}`);
        }
        setUploading(false);
    };

    const handleDelete = () => {
        setImageUrl("");
        setMessage("");
        setFileName("");
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        onUpload("");
    };

    if (!open) return null;

    const displayImage = previewUrl || imageUrl;

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeIn 0.2s ease",
            padding: "12px"
        }}
        onClick={onClose}
        >
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from { 
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to { 
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                padding: "20px",
                width: "100%",
                maxWidth: 420,
                position: "relative",
                fontFamily: "Inter, Segoe UI, Arial, sans-serif",
                animation: "slideUp 0.3s ease"
            }}
            onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    marginBottom: 20,
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#1e293b",
                    textAlign: "center",
                    letterSpacing: -0.3
                }}>
                    {title}
                </div>

                {/* Image Preview */}
                {displayImage && (
                    <div style={{
                        position: "relative",
                        marginBottom: 20,
                        borderRadius: 12,
                        overflow: "hidden",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                        border: previewUrl ? "2px solid #667eea" : "2px solid #e2e8f0"
                    }}>
                        <img src={displayImage} alt="Document" style={{
                            width: "100%",
                            borderRadius: 12,
                            display: "block",
                            objectFit: "contain",
                            maxHeight: 300,
                            background: "#f8fafc"
                        }} />
                        {previewUrl && (
                            <div style={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "#fff",
                                padding: "4px 12px",
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 600,
                                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)"
                            }}>
                                Preview
                            </div>
                        )}
                        {imageUrl && !previewUrl && (
                            <button
                                type="button"
                                onClick={handleDelete}
                                style={{
                                    position: "absolute",
                                    top: 12,
                                    right: 12,
                                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: 36,
                                    height: 36,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    fontWeight: 700,
                                    fontSize: 20,
                                    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                                    transition: "transform 0.2s ease"
                                }}
                                title={t("Common.delete", "Delete")}
                                disabled={uploading}
                                onMouseEnter={(e) => !uploading && (e.target.style.transform = "scale(1.1)")}
                                onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}

                {/* File Input Section - Only show if no preview */}
                {!displayImage && (
                    <div style={{
                        marginBottom: 20,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12
                    }}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={accept}
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                            disabled={uploading}
                            id="document-upload"
                        />
                        <label
                            htmlFor="document-upload"
                            style={{
                                cursor: uploading ? "not-allowed" : "pointer",
                                opacity: uploading ? 0.6 : 1,
                                width: "100%"
                            }}
                        >
                            <div
                                style={{
                                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                    border: "3px dashed #cbd5e1",
                                    borderRadius: 12,
                                    padding: "32px 20px",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 12,
                                    transition: "all 0.2s ease",
                                    pointerEvents: uploading ? "none" : "auto"
                                }}
                                onMouseEnter={(e) => {
                                    if (!uploading) {
                                        e.currentTarget.style.borderColor = "#667eea";
                                        e.currentTarget.style.background = "#f8fafc";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "#cbd5e1";
                                    e.currentTarget.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                                }}
                            >
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                    <polyline points="21 15 16 10 5 21"/>
                                </svg>
                                <div style={{
                                    fontSize: 15,
                                    color: "#64748b",
                                    fontWeight: 600
                                }}>
                                    {t("Common.tap_to_select", "Tap to select document")}
                                </div>
                            </div>
                        </label>
                    </div>
                )}

                {/* File name display when image is shown */}
                {displayImage && fileName && (
                    <div style={{
                        fontSize: 14,
                        color: "#64748b",
                        fontWeight: 500,
                        textAlign: "center",
                        marginBottom: 16,
                        wordBreak: "break-word",
                        padding: "8px 12px",
                        background: "#f8fafc",
                        borderRadius: 8
                    }}>
                        {fileName}
                    </div>
                )}

                {/* Success/Error Message */}
                {message && (
                    <div style={{
                        color: message.includes("success") ? "#10b981" : "#ef4444",
                        marginBottom: 20,
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: 14,
                        padding: "10px 16px",
                        borderRadius: 8,
                        background: message.includes("success") 
                            ? "rgba(16, 185, 129, 0.1)" 
                            : "rgba(239, 68, 68, 0.1)"
                    }}>
                        {message}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: "flex",
                    gap: 12,
                    marginTop: 8
                }}>
                    {/* Show Upload button only when file is selected (preview mode) */}
                    {previewUrl && selectedFile && (
                        <button
                            onClick={handleUpload}
                            style={{
                                flex: 1,
                                background: uploading 
                                    ? "#cbd5e1"
                                    : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "#fff",
                                border: "none",
                                padding: "12px 24px",
                                borderRadius: 10,
                                fontWeight: 600,
                                cursor: uploading ? "not-allowed" : "pointer",
                                fontSize: 15,
                                boxShadow: uploading 
                                    ? "none"
                                    : "0 4px 14px rgba(16, 185, 129, 0.3)",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8
                            }}
                            disabled={uploading}
                            onMouseEnter={(e) => {
                                if (!uploading) {
                                    e.target.style.transform = "translateY(-2px)";
                                    e.target.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.4)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow = uploading 
                                    ? "none"
                                    : "0 4px 14px rgba(16, 185, 129, 0.3)";
                            }}
                        >
                            {uploading ? (
                                <>
                                    <div style={{
                                        width: 16,
                                        height: 16,
                                        border: "2px solid #fff",
                                        borderTopColor: "transparent",
                                        borderRadius: "50%",
                                        animation: "spin 0.6s linear infinite"
                                    }} />
                                    <span>{t("Common.uploading", "Uploading...")}</span>
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="17 8 12 3 7 8" />
                                        <line x1="12" y1="3" x2="12" y2="15" />
                                    </svg>
                                    <span>{t("Common.upload", "Upload")}</span>
                                </>
                            )}
                        </button>
                    )}
                    
                    {/* Show Change button if preview exists */}
                    {previewUrl && (
                        <label
                            htmlFor="document-upload"
                            style={{
                                flex: selectedFile ? "none" : 1,
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "#fff",
                                border: "none",
                                padding: "12px 24px",
                                borderRadius: 10,
                                fontWeight: 600,
                                cursor: uploading ? "not-allowed" : "pointer",
                                fontSize: 15,
                                transition: "all 0.2s ease",
                                textAlign: "center",
                                boxShadow: "0 4px 14px rgba(102, 126, 234, 0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                            onMouseEnter={(e) => {
                                if (!uploading) {
                                    e.target.style.transform = "translateY(-2px)";
                                    e.target.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.4)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow = "0 4px 14px rgba(102, 126, 234, 0.3)";
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={accept}
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                                disabled={uploading}
                                id="document-upload"
                            />
                            Change
                        </label>
                    )}

                    <button
                        onClick={onClose}
                        style={{
                            flex: (selectedFile || !displayImage) ? "none" : 1,
                            background: "#fff",
                            color: "#64748b",
                            border: "2px solid #e2e8f0",
                            padding: "12px 24px",
                            borderRadius: 10,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontSize: 15,
                            transition: "all 0.2s ease"
                        }}
                        disabled={uploading}
                        onMouseEnter={(e) => {
                            if (!uploading) {
                                e.target.style.borderColor = "#cbd5e1";
                                e.target.style.color = "#475569";
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = "#e2e8f0";
                            e.target.style.color = "#64748b";
                        }}
                    >
                        {t("Common.close", "Close")}
                    </button>
                </div>
            </div>
        </div>
    );
}